import { createClient } from '@supabase/supabase-js';
import { type Database } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://shshvtppenarusdfglps.supabase.co';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNoc2h2dHBwZW5hcnVzZGZnbHBzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc2MjM5ODUsImV4cCI6MjA1MzE5OTk4NX0.MaLjtl8lbfv567Z6dYDTD9FJm11jrScWxMUQDedxCAE';

console.log('Initializing Supabase client with URL:', supabaseUrl);

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseKey,
  { 
    auth: {
      persistSession: true,
      autoRefreshToken: true
    },
    global: {
      // Add a custom fetch with timeout to prevent hanging requests
      fetch: (...args) => {
        const [url, options = {}] = args;
        
        return new Promise((resolve, reject) => {
          // Set a 15 second timeout for all requests
          const timeout = setTimeout(() => {
            reject(new Error(`Request timed out for ${url}`));
          }, 15000);
          
          fetch(url, options)
            .then(response => {
              clearTimeout(timeout);
              resolve(response);
            })
            .catch(error => {
              clearTimeout(timeout);
              reject(error);
            });
        });
      }
    }
  }
);

// Test connection function
export async function testConnection() {
  try {
    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection test timed out')), 10000);
    });

    // Try a simple non-RLS-protected query first - don't use .single() as table might be empty
    const connectionPromise = supabase
      .from('review_settings')
      .select('id')
      .limit(1);

    const { data, error: settingsError } = await Promise.race([
      connectionPromise,
      timeoutPromise as Promise<any>
    ]);

    if (settingsError) {
      console.error('Connection test failed (settings):', settingsError);
      return false;
    }

    // Connection is successful whether we get data or not (empty table is fine)
    console.log('Connection test successful:', data ? `Found ${data.length} rows` : 'Table is empty');
    return true;
  } catch (error) {
    console.error('Failed to connect to Supabase:', error instanceof Error ? error.message : error);
    throw error;
  }
}

// Helper function to check if user is admin
export async function isAdmin(email: string): Promise<boolean> {
  return ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(email);
}

// Function to safely get or create user profile
export async function getUserProfile() {
  try {
    
    // Get the current user session
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      
      return null;
    }

    const email = user.email || '';
    const userId = user.id;
    
    const storedLevel = localStorage.getItem('currentLevel');
    

    // Try to get existing profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, user_id, email, target_language, native_language, current_level, is_admin, has_seen_guide')
      .eq('user_id', userId)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError);
      return null;
    }

    // If profile doesn't exist, create it
    if (!profile) {
      // Get level from localStorage or default to A1
      const level = (storedLevel && storedLevel.trim() !== '') ? storedLevel : 'A1';
      
      
      const defaultProfile = {
        user_id: user.id,
        email: email,
        target_language: localStorage.getItem('targetLanguage')?.trim() || 'Dutch',
        native_language: localStorage.getItem('nativeLanguage')?.trim() || 'English',
        current_level: level,
        is_admin: await isAdmin(email),
        has_seen_guide: localStorage.getItem('hasSeenGuide') === 'true'
      };

      // Create the profile in the database
      const { data: newProfile, error } = await supabase
        .from('profiles')
        .insert([defaultProfile])
        .select()
        .single();

      if (error) {
        console.error('Error creating profile:', error);
        return defaultProfile; 
      }

      // Store the level in localStorage
      localStorage.setItem('currentLevel', level);
      
      return newProfile;
    }

    // Ensure the profile has a current_level
    
    
    // If profile has a level but localStorage doesn't, update localStorage
    if (profile.current_level && profile.current_level.trim() !== '' && 
        (!storedLevel || storedLevel.trim() === '')) {
      
      localStorage.setItem('currentLevel', profile.current_level);
    } 
    // If localStorage has a level but profile doesn't, update profile
    else if ((!profile.current_level || profile.current_level.trim() === '') && 
             storedLevel && storedLevel.trim() !== '') {
      
      
      const { error } = await supabase
        .from('profiles')
        .upsert({ user_id: userId, current_level: storedLevel })
        .update({ current_level: storedLevel })
        .eq('user_id', userId);
        
      if (error) {
        console.error('Error updating profile level:', error);
      } else {
        profile.current_level = storedLevel;
      }
    }

    
    
    // Ensure we have a valid level in both places
    if (profile.current_level && profile.current_level.trim() !== '') {
      localStorage.setItem('currentLevel', profile.current_level);
      
    } else if (storedLevel && storedLevel.trim() !== '') {
      profile.current_level = storedLevel;
      
    } else {
      profile.current_level = 'A1';
      localStorage.setItem('currentLevel', 'A1');
      
    }

    
    
    return profile;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    // If there's an error, try to use localStorage as a fallback
    const storedLevel = localStorage.getItem('currentLevel');
    if (storedLevel && storedLevel.trim() !== '') {
      
      return {
        current_level: storedLevel
      };
    }
    return null;
  }
}


// Helper function to get the correct table based on native language
export function getUnknownWordsTable(): string {
  const nativeLanguage = localStorage.getItem('nativeLanguage')?.trim();
  if (!nativeLanguage) {
    console.warn('No native language found in localStorage, defaulting to English');
    localStorage.setItem('nativeLanguage', 'English');
    return 'lesson_unknown_words';
    
  }
  return nativeLanguage === 'Persian' ? 'persian_lesson_unknown_words' : 'lesson_unknown_words';
}

// Helper function to get the correct lessons table based on native language
export function getLessonsTable(language?: string): string {
  const nativeLanguage = language || localStorage.getItem('nativeLanguage')?.trim();
  if (!nativeLanguage) {
    console.warn('No native language found in localStorage, defaulting to English');
    if (!language) {
      localStorage.setItem('nativeLanguage', 'English');
    }

    return 'lesson_content';
  }

  return nativeLanguage === 'Persian' ? 'persian_lesson_content' : 'lesson_content';
}

// Helper function to get the correct words table based on native language
export function getWordsTable(language?: string): string {
  const nativeLanguage = language || localStorage.getItem('nativeLanguage')?.trim();
  if (!nativeLanguage) {
    console.warn('No native language found in localStorage, defaulting to words');
    if (!language) {
      localStorage.setItem('nativeLanguage', 'English');
    }

    return 'words';
  }

  return nativeLanguage === 'Persian' ? 'persian_words' : 'words';
}

// Helper function to get the correct progress table based on native language
export function getProgressTable(language?: string): string {
  const nativeLanguage = language || localStorage.getItem('nativeLanguage');

  if (!nativeLanguage) {
    console.warn('No native language found in localStorage, defaulting to English');
    if (!language) {
      localStorage.setItem('nativeLanguage', 'English');
    }

    return 'lesson_progress';
  }

  return nativeLanguage.trim() === 'Persian' ? 'persian_lesson_progress' : 'lesson_progress';
}

// Unknown words functions
export async function getUnknownWords(userId: string, lessonId: string): Promise<string[]> {
  
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.error('No active session found');
    return [];
  }

  const { data, error } = await supabase
    .from(getUnknownWordsTable())
    .select('word')
    .eq('user_id', userId)
    .eq('lesson_id', lessonId);

  if (error) {
    console.error('Error fetching unknown words:', error);
    return [];
  }

  
  return data.map(item => item.word);
}

export async function addUnknownWord(userId: string, lessonId: string, word: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.error('No active session found');
    return;
  }

  
  const table = getUnknownWordsTable();
  
  // Get the lesson level first
  const lessonsTable = getLessonsTable();
  
  const { data: lesson, error: lessonError } = await supabase
    .from(lessonsTable)
    .select('level')
    .eq('id', lessonId)
    .single();

  if (lessonError) {
    console.error('Error getting lesson level:', lessonError);
    return;
  }

  if (!lesson) {
    console.error('Lesson not found when adding unknown word');
    return;
  }

  const { error } = await supabase
    .from(table)
    .insert([
      {
        user_id: userId,
        lesson_id: lessonId,
        word: word.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim(),
        level: lesson.level
      }
    ]);

  if (error) {
    // If the error is a duplicate key violation, it's not a problem
    if (error.code === '23505') {
      console.log(`Word already marked as unknown: ${word}`);
      
    } else {
      console.error('Error adding unknown word:', error);
    }
  }
}

export async function removeUnknownWord(userId: string, lessonId: string, word: string): Promise<void> {
  
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.error('No active session found');
    return;
  }

  const { error } = await supabase
    .from(getUnknownWordsTable())
    .delete()
    .eq('user_id', userId)
    .eq('lesson_id', lessonId)
    .eq('word', word.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim());

  if (error) {
    console.error('Error removing unknown word:', error);
    
  }
}